/**
 * ABRSM Import Dialog
 * -------------------
 * Imports sight-reading exercises from cXML (MusicXML) files
 * and maps grade to in-app difficulty.
 */

import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, FileText } from "lucide-react";
import type { Difficulty } from "@/lib/exerciseGenerator";

interface ABRSMUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (config: ABRSMExerciseConfig) => Promise<void> | void;
}

export interface ABRSMExerciseConfig {
  grade: number;
  difficulty: Difficulty;
  tempoHint: number;
  cxmlFile: File;
  startBar: number | null;
  endBar: number | null;
}

const ABRSM_GRADES: Record<number, {
  name: string;
  difficulty: Difficulty;
  tempo: [number, number];
}> = {
  1: { name: "Grade 1", difficulty: 1, tempo: [60, 80] },
  2: { name: "Grade 2", difficulty: 2, tempo: [66, 88] },
  3: { name: "Grade 3", difficulty: 2, tempo: [72, 96] },
  4: { name: "Grade 4", difficulty: 3, tempo: [80, 100] },
  5: { name: "Grade 5", difficulty: 3, tempo: [84, 108] },
  6: { name: "Grade 6", difficulty: 4, tempo: [92, 112] },
  7: { name: "Grade 7", difficulty: 4, tempo: [96, 120] },
  8: { name: "Grade 8", difficulty: 5, tempo: [100, 126] },
};

function isCxmlLikeFile(file: File): boolean {
  return /\.(cxml|xml|musicxml)$/i.test(file.name);
}

export default function ABRSMUploadDialog({ isOpen, onClose, onUpload }: ABRSMUploadDialogProps) {
  const [grade, setGrade] = useState<number>(1);
  const [cxmlFile, setCxmlFile] = useState<File | null>(null);
  const [startBarInput, setStartBarInput] = useState<string>("");
  const [endBarInput, setEndBarInput] = useState<string>("");
  const [errorText, setErrorText] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const gradeStandards = ABRSM_GRADES[grade];

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;

    if (!isCxmlLikeFile(file)) {
      setErrorText("Please select a .cxml, .musicxml, or .xml file.");
      return;
    }

    setCxmlFile(file);
    setErrorText("");
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!cxmlFile) {
      setErrorText("Select a cXML file to import.");
      return;
    }

    const startBar = startBarInput.trim() ? Number(startBarInput) : null;
    const endBar = endBarInput.trim() ? Number(endBarInput) : null;

    if (startBar !== null && (!Number.isFinite(startBar) || startBar < 1)) {
      setErrorText("Start bar must be a positive number.");
      return;
    }

    if (endBar !== null && (!Number.isFinite(endBar) || endBar < 1)) {
      setErrorText("End bar must be a positive number.");
      return;
    }

    if (startBar !== null && endBar !== null && endBar < startBar) {
      setErrorText("End bar must be greater than or equal to start bar.");
      return;
    }

    setIsSubmitting(true);
    setErrorText("");

    try {
      await onUpload({
        grade,
        difficulty: gradeStandards.difficulty,
        tempoHint: Math.round((gradeStandards.tempo[0] + gradeStandards.tempo[1]) / 2),
        cxmlFile,
        startBar,
        endBar,
      });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to import cXML file.";
      setErrorText(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [cxmlFile, startBarInput, endBarInput, grade, gradeStandards, onUpload, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ background: "oklch(0 0 0 / 60%)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-lg mx-4 rounded-lg overflow-hidden"
          style={{
            background: "oklch(0.19 0.015 270)",
            border: "1px solid oklch(0.28 0.010 270)",
            boxShadow: "0 8px 32px oklch(0 0 0 / 40%)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="flex items-center justify-between px-5 py-4 border-b"
            style={{ borderColor: "oklch(0.28 0.010 270)" }}
          >
            <div>
              <h3 className="text-lg font-bold" style={{ color: "oklch(0.90 0.01 270)" }}>
                Import ABRSM cXML Exercise
              </h3>
              <p className="text-xs mt-0.5" style={{ color: "oklch(0.50 0.01 270)" }}>
                Load a cXML/MusicXML score and optionally practice a bar range
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded flex items-center justify-center transition-colors"
              style={{ background: "oklch(0.22 0.012 270)", color: "oklch(0.60 0.01 270)" }}
            >
              <X size={16} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold mb-2" style={{ color: "oklch(0.70 0.01 270)" }}>
                cXML File (Required)
              </label>
              <div
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors hover:border-opacity-60"
                style={{
                  borderColor: cxmlFile ? "oklch(0.70 0.18 150)" : "oklch(0.28 0.010 270)",
                  background: cxmlFile ? "oklch(0.70 0.18 150 / 5%)" : "oklch(0.22 0.012 270)",
                }}
                onClick={() => document.getElementById("cxml-upload")?.click()}
              >
                <input
                  id="cxml-upload"
                  type="file"
                  accept=".cxml,.musicxml,.xml,application/xml,text/xml"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                {cxmlFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText size={20} style={{ color: "oklch(0.70 0.18 150)" }} />
                    <span className="text-sm font-mono" style={{ color: "oklch(0.70 0.18 150)" }}>
                      {cxmlFile.name}
                    </span>
                  </div>
                ) : (
                  <>
                    <Upload size={24} className="mx-auto mb-2" style={{ color: "oklch(0.50 0.01 270)" }} />
                    <p className="text-sm" style={{ color: "oklch(0.60 0.01 270)" }}>
                      Click to upload cXML
                    </p>
                    <p className="text-xs mt-1" style={{ color: "oklch(0.40 0.01 270)" }}>
                      Supported: .cxml, .musicxml, .xml
                    </p>
                  </>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-2" style={{ color: "oklch(0.70 0.01 270)" }}>
                ABRSM Grade
              </label>
              <Select value={String(grade)} onValueChange={(v) => setGrade(Number(v))}>
                <SelectTrigger className="w-full border-border" style={{ background: "oklch(0.22 0.012 270)" }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ABRSM_GRADES).map(([gradeNum, info]) => (
                    <SelectItem key={gradeNum} value={gradeNum}>
                      {info.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs mt-1" style={{ color: "oklch(0.45 0.01 270)" }}>
                Difficulty: Level {gradeStandards.difficulty} • Tempo hint: {gradeStandards.tempo[0]}-{gradeStandards.tempo[1]} BPM
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-2" style={{ color: "oklch(0.70 0.01 270)" }}>
                  Start Bar (Optional)
                </label>
                <input
                  type="number"
                  min={1}
                  value={startBarInput}
                  onChange={(e) => setStartBarInput(e.target.value)}
                  className="w-full h-9 rounded px-3 text-sm border"
                  style={{
                    background: "oklch(0.22 0.012 270)",
                    borderColor: "oklch(0.28 0.010 270)",
                    color: "oklch(0.88 0.01 270)",
                  }}
                  placeholder="e.g. 9"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold mb-2" style={{ color: "oklch(0.70 0.01 270)" }}>
                  End Bar (Optional)
                </label>
                <input
                  type="number"
                  min={1}
                  value={endBarInput}
                  onChange={(e) => setEndBarInput(e.target.value)}
                  className="w-full h-9 rounded px-3 text-sm border"
                  style={{
                    background: "oklch(0.22 0.012 270)",
                    borderColor: "oklch(0.28 0.010 270)",
                    color: "oklch(0.88 0.01 270)",
                  }}
                  placeholder="e.g. 16"
                />
              </div>
            </div>

            <div
              className="rounded-lg p-3 text-xs leading-relaxed"
              style={{
                background: "oklch(0.82 0.14 200 / 10%)",
                border: "1px solid oklch(0.82 0.14 200 / 20%)",
                color: "oklch(0.70 0.01 270)",
              }}
            >
              <strong style={{ color: "oklch(0.82 0.14 200)" }}>Note:</strong> The score is parsed from cXML.
              If start/end bars are provided, only that bar range is imported.
            </div>

            {errorText && (
              <div
                className="rounded-md px-3 py-2 text-xs"
                style={{
                  background: "oklch(0.65 0.24 25 / 12%)",
                  border: "1px solid oklch(0.65 0.24 25 / 30%)",
                  color: "oklch(0.65 0.24 25)",
                }}
              >
                {errorText}
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-2 px-5 py-4 border-t"
            style={{ borderColor: "oklch(0.28 0.010 270)" }}
          >
            <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!cxmlFile || isSubmitting}>
              {isSubmitting ? "Importing..." : "Import Exercise"}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
